import { useState, useEffect } from "react";
import API from "../lib/api";
import { Warning } from "@phosphor-icons/react";

const REASON_LABELS = {
  client_unresponsive: "Client unresponsive",
  supplier_holiday:    "Supplier holiday / unavailable",
  complex_quote:       "Complex / waiting on info",
  out_of_hours:        "Out of hours / weekend",
  other:               "Other",
  Unspecified:         "No reason logged",
};
const REASON_COLOURS = [
  "#dc2626", "#ea580c", "#d97706", "#65a30d", "#0891b2", "#7c3aed", "#db2777",
];

/**
 * Phase 258 — Aggregated breach reasons for the last 30d.
 * Shows a simple horizontal bar chart + clickable legend for examples.
 */
export default function BreachInsightsWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/sla/breach-insights?days=30")
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return null;
  if (!data.total_breached) return null;

  const max = Math.max(...data.breakdown.map(b => b.count), 1);

  return (
    <div className="border border-border rounded-lg p-4 bg-card mb-6 animate-fade-in" data-testid="breach-insights-widget">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold flex items-center gap-1.5 font-[Chivo]">
          <Warning size={14} className="text-red-600" /> Breach Insights — last 30 days
        </h3>
        <span className="text-[10px] text-muted-foreground tabular-nums">{data.total_breached} item(s)</span>
      </div>

      <div className="space-y-2">
        {data.breakdown.map((b, i) => {
          const pct = (b.count / max) * 100;
          const colour = REASON_COLOURS[i % REASON_COLOURS.length];
          return (
            <div key={b.reason} data-testid={`breach-row-${b.reason}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium">{REASON_LABELS[b.reason] || b.reason}</span>
                <span className="text-[10px] tabular-nums text-muted-foreground">{b.count} ({Math.round((b.count / data.total_breached) * 100)}%)</span>
              </div>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div style={{ width: `${pct}%`, background: colour }} className="h-full transition-all" />
              </div>
              {b.examples?.length > 0 && (
                <details className="mt-1">
                  <summary className="text-[10px] text-muted-foreground hover:text-foreground cursor-pointer">View examples</summary>
                  <ul className="mt-1 ml-3 text-[10px] text-muted-foreground space-y-0.5">
                    {b.examples.map(ex => (
                      <li key={ex.id}>{ex.reference} — {ex.title}{ex.other ? ` • "${ex.other}"` : ""}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
